import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Toolbar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
} from '@mui/material';

import { DataGrid, GridToolbar, type GridColDef } from '@mui/x-data-grid';
import type { GridRenderCellParams } from '@mui/x-data-grid';

import LoadingMask from '@/components/shared/LoadingMask';

import { useApi } from '@/providers/ApiContext';
import { getManifestAssets } from '@/actions/manifest';
import { getInspectionAssets } from '@/actions/inspectionAsset';
import { getDocumentAssets } from '@/actions/documentAsset';
import AccQsureAdminTraceAsset from './AccQsureAdminTraceAsset';
import type { AssetListResponse, Asset, EntityId } from '@/types';

type EntityType = 'manifest' | 'inspection' | 'document' | 'chart' | 'plot';

interface EntityTypeOption {
  value: EntityType;
  label: string;
}

interface SelectedTraceAsset {
  entityType: EntityType;
  entityId: EntityId;
  assetKey: string;
}

interface AssetRow extends Asset {
  id: number;
  [key: string]: unknown;
}

function AccQsureAdminAssets(): React.JSX.Element {
  const api = useApi();

  const [entityType, setEntityType] = useState<EntityType | ''>('');
  const [entityId, setEntityId] = useState<string>('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [selectedTraceAsset, setSelectedTraceAsset] = useState<SelectedTraceAsset | null>(null);

  const entityTypes: EntityTypeOption[] = [
    { value: 'manifest', label: 'Manifest' },
    { value: 'inspection', label: 'Inspection' },
    { value: 'document', label: 'Document' },
    { value: 'chart', label: 'Chart' },
    { value: 'plot', label: 'Plot' },
  ];

  const isValidEntityId = entityId.length === 24;
  const canList = entityType && isValidEntityId;

  const handleListAssets = async (): Promise<void> => {
    if (!canList || !entityType || !api) return;

    setLoading(true);
    setError('');
    setAssets([]);
    setSelectedTraceAsset(null);

    try {
      let response: AssetListResponse;

      switch (entityType) {
        case 'manifest':
          response = await getManifestAssets(api, entityId as EntityId);
          break;
        case 'inspection':
          response = await getInspectionAssets(api, entityId as EntityId);
          break;
        case 'document':
          response = await getDocumentAssets(api, entityId as EntityId);
          break;
        default:
          throw new Error('Invalid entity type');
      }

      setAssets(Array.isArray(response.assets) ? response.assets : [response.assets as unknown as Asset]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch assets';
      setError(errorMessage);
      console.error('Error fetching assets:', err);
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef<AssetRow>[] = useMemo(() => {
    if (assets.length === 0) return [];

    // Dynamically generate columns based on the first asset's properties
    const first_asset = assets[0];
    if (first_asset == null || typeof first_asset !== 'object') return [];
    return Object.keys(first_asset).map((key) => ({
      field: key,
      headerName: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<AssetRow>) => {
        const value = params.value;

        // Special handling for trace.json assets
        if (key === 'asset_id' && value === 'trace.json') {
          return (
            <Box
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': { color: 'primary.dark' },
              }}
              onClick={() => {
                setSelectedTraceAsset({
                  entityType: entityType as EntityType,
                  entityId: entityId as EntityId,
                  assetKey: (params.row['key'] as string) || String(value),
                });
              }}
            >
              {String(value)} (click to view)
            </Box>
          );
        }

        if (typeof value === 'object' && value !== null) {
          return JSON.stringify(value);
        }
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }
        if (value === null || value === undefined) {
          return '-';
        }
        return String(value);
      },
    }));
  }, [assets, entityType, entityId]);

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <Typography variant='h6' sx={{ mr: 4 }}>
          Asset Viewer
        </Typography>
      </Toolbar>
      <Divider />

      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Asset Search
          </Typography>

          <Grid container spacing={3} alignItems='flex-end'>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={entityType}
                  label='Entity Type'
                  onChange={(e) => setEntityType(e.target.value as EntityType)}
                >
                  {entityTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label='Entity ID'
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                placeholder='24 character string'
                error={entityId.length > 0 && !isValidEntityId}
                helperText={
                  entityId.length > 0 && !isValidEntityId
                    ? 'Must be exactly 24 characters'
                    : ''
                }
                inputProps={{ maxLength: 24 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Button
                variant='contained'
                onClick={handleListAssets}
                disabled={!canList || loading}
              >
                {loading ? 'Loading...' : 'List Assets'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && <LoadingMask />}

        {assets.length > 0 && !loading && (
          <Paper sx={{ p: 3 }}>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Assets ({assets.length})
            </Typography>

            <DataGrid
              rows={assets.map((asset, index) => ({ id: index, ...asset } as AssetRow))}
              columns={columns}
              paginationModel={{ page: 0, pageSize: 10 }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              showToolbar
              slots={{
                toolbar: GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Paper>
        )}

        {selectedTraceAsset && selectedTraceAsset.entityType !== 'chart' && selectedTraceAsset.entityType !== 'plot' && (
          <AccQsureAdminTraceAsset
            entityType={selectedTraceAsset.entityType as 'manifest' | 'inspection' | 'document'}
            entityId={selectedTraceAsset.entityId}
            assetKey={selectedTraceAsset.assetKey}
          />
        )}
      </Box>
    </>
  );
}

export default AccQsureAdminAssets;
